import type {
  FriendsListResponse,
  UsernameSearchResponse,
} from '../api/friends-types.js';
import { apiBaseUrl } from '../platform/index.js';
import { getAuth, onAuthChange } from './auth.js';
import { supabaseBrowser } from './supabaseClient.js';

/**
 * Friends client store — mirrors profile.ts shape. Caches the
 * current user's friends list (accepted + incoming pending +
 * outgoing pending), exposes a listener API for scenes to re-render
 * on changes, and provides thin wrappers around the mutation
 * endpoints that refresh the cache after a write.
 *
 * Why a store at all: FriendsScene needs the data, but so will any
 * future "online now" badges, friend-count chip on Home, etc. One
 * cache + one listener pattern beats N components fetching
 * independently.
 *
 * Lifecycle:
 *   - main.ts calls initFriends() at boot, AFTER initAuth() +
 *     initProfile() so the auth listener has somewhere to land.
 *   - Auto-fetches on sign-in, clears on sign-out.
 *   - Mutations (sendFriendRequest, accept, reject, unfriend) post
 *     to the API and re-fetch on success so listeners observe the
 *     new state.
 */

export type FriendsState =
  | { readonly kind: 'unloaded' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly list: FriendsListResponse }
  | { readonly kind: 'error'; readonly error: string };

const UNLOADED: FriendsState = { kind: 'unloaded' };

let cachedState: FriendsState = UNLOADED;
let initialized = false;
const listeners = new Set<(s: FriendsState) => void>();

function setState(next: FriendsState): void {
  cachedState = next;
  for (const fn of listeners) {
    try {
      fn(cachedState);
    } catch (e) {
      console.error('friends listener threw', e);
    }
  }
}

export function initFriends(): void {
  if (initialized) return;
  initialized = true;

  onAuthChange((auth) => {
    if (auth.user) {
      void fetchFriends();
    } else {
      setState(UNLOADED);
    }
  });

  // initAuth() may already have resolved by the time we run; kick
  // off the initial fetch if so.
  if (getAuth().user) {
    void fetchFriends();
  }
}

export function getFriends(): FriendsState {
  return cachedState;
}

export function onFriendsChange(
  fn: (s: FriendsState) => void
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// ============================================================
// Network ops
// ============================================================

async function authHeader(): Promise<HeadersInit> {
  const supabase = supabaseBrowser();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchFriends(): Promise<void> {
  setState({ kind: 'loading' });
  try {
    const res = await fetch(`${apiBaseUrl()}/api/friends`, {
      headers: await authHeader(),
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      setState({ kind: 'error', error: `${res.status}: ${msg}` });
      return;
    }
    const body = (await res.json()) as FriendsListResponse;
    setState({ kind: 'loaded', list: body });
  } catch (err: unknown) {
    setState({
      kind: 'error',
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/** Force a refetch — used after the store thinks it's stale. */
export function refreshFriends(): void {
  void fetchFriends();
}

export type MutationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: string };

/**
 * POST /api/friends — send a friend request to the username.
 * Refreshes the cache on success so the outgoing list updates.
 */
export async function sendFriendRequest(
  username: string
): Promise<MutationResult> {
  try {
    const res = await fetch(`${apiBaseUrl()}/api/friends`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(await authHeader()),
      },
      body: JSON.stringify({ username }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
      } | null;
      return { ok: false, error: body?.error ?? `${res.status}` };
    }
    void fetchFriends();
    return { ok: true };
  } catch (err: unknown) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export async function acceptFriendRequest(
  otherUserId: string
): Promise<MutationResult> {
  return patchFriendship(otherUserId, 'accept');
}

export async function rejectFriendRequest(
  otherUserId: string
): Promise<MutationResult> {
  return patchFriendship(otherUserId, 'reject');
}

async function patchFriendship(
  otherUserId: string,
  action: 'accept' | 'reject'
): Promise<MutationResult> {
  try {
    const res = await fetch(
      `${apiBaseUrl()}/api/friends/${encodeURIComponent(otherUserId)}`,
      {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json',
          ...(await authHeader()),
        },
        body: JSON.stringify({ action }),
      }
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
      } | null;
      return { ok: false, error: body?.error ?? `${res.status}` };
    }
    void fetchFriends();
    return { ok: true };
  } catch (err: unknown) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * DELETE /api/friends/:otherUserId — unfriend OR cancel outgoing
 * request. Same endpoint, different semantics depending on the row's
 * current state — the server handles both.
 */
export async function unfriend(
  otherUserId: string
): Promise<MutationResult> {
  try {
    const res = await fetch(
      `${apiBaseUrl()}/api/friends/${encodeURIComponent(otherUserId)}`,
      {
        method: 'DELETE',
        headers: await authHeader(),
      }
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
      } | null;
      return { ok: false, error: body?.error ?? `${res.status}` };
    }
    void fetchFriends();
    return { ok: true };
  } catch (err: unknown) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * GET /api/users/search?q=<prefix> — typeahead lookup. Used by
 * FriendsScene's ADD tab. Does NOT touch the cache; returns the
 * search results directly to the caller.
 */
export async function searchUsernames(
  prefix: string
): Promise<UsernameSearchResponse | null> {
  try {
    const res = await fetch(
      `${apiBaseUrl()}/api/users/search?q=${encodeURIComponent(prefix)}`,
      { headers: await authHeader() }
    );
    if (!res.ok) return null;
    return (await res.json()) as UsernameSearchResponse;
  } catch (err: unknown) {
    console.warn('searchUsernames failed', err);
    return null;
  }
}
